import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BellRing, Trash2, ExternalLink, CalendarClock, Globe, Coins, Compass } from "lucide-react";

function daysUntil(s) {
  if (!s) return null;
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / (1000*60*60*24));
}
function urgency(d) {
  if (d === null || d === undefined) return { label: "Deadline TBD", cls: "bg-secondary text-foreground" };
  if (d < 0) return { label: `Closed ${Math.abs(d)}d ago`, cls: "bg-neutral-200 text-neutral-700" };
  if (d <= 7) return { label: `Closes in ${d}d`, cls: "bg-red-100 text-red-800 border border-red-300" };
  if (d <= 30) return { label: `Closes in ${d}d`, cls: "bg-amber-100 text-amber-900 border border-amber-300" };
  if (d <= 90) return { label: `Closes in ${d}d`, cls: "bg-emerald-100 text-emerald-900 border border-emerald-300" };
  return { label: `Closes in ${d}d`, cls: "bg-secondary" };
}

export default function Alerts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/funding/alerts").then(r => { setItems(r.data); setLoading(false); });
  useEffect(() => { load(); }, []);
  const del = async (id) => { await api.delete(`/funding/alerts/${id}`); load(); };

  const withDays = items.map(it => ({ ...it, _d: daysUntil(it.deadline) })).sort((a,b)=>{
    if (a._d === null) return 1; if (b._d === null) return -1; return a._d - b._d;
  });

  return (
    <div className="max-w-5xl mx-auto p-8">
      <div className="flex items-start justify-between gap-6 mb-8">
        <div>
          <div className="overline">Notifications</div>
          <h1 className="font-display text-5xl mt-2 flex items-center gap-3"><BellRing className="w-10 h-10 text-primary"/>Funding alerts</h1>
          <p className="text-muted-foreground mt-2 max-w-2xl">Every call you save appears here with its closing date. The most urgent ones rise to the top.</p>
        </div>
        <Link to="/app/funding">
          <Button variant="outline" className="rounded-full" data-testid="btn-back-funding"><Compass className="w-4 h-4 mr-2"/>Find more</Button>
        </Link>
      </div>

      {loading ? (
        <div className="text-muted-foreground">Loading…</div>
      ) : withDays.length === 0 ? (
        <div className="card-crisp rounded-lg p-12 text-center">
          <BellRing className="w-10 h-10 text-primary mx-auto mb-3"/>
          <div className="font-display text-2xl">No alerts yet</div>
          <div className="text-muted-foreground mt-1">Save an opportunity from the funding feed and it will appear here.</div>
        </div>
      ) : (
        <div className="space-y-3">
          {withDays.map((it) => {
            const u = urgency(it._d);
            return (
              <div key={it.id} className="card-crisp rounded-lg p-5 flex items-start gap-5" data-testid={`alert-${it.id}`}>
                <div className={`min-w-[110px] text-center px-3 py-3 rounded-md ${u.cls}`}>
                  <div className="text-xs uppercase tracking-wider">Status</div>
                  <div className="text-sm font-semibold mt-1">{u.label}</div>
                  <div className="text-[11px] mt-1 opacity-80">{it.deadline || "TBD"}</div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="overline">{it.agency}</div>
                  <div className="font-display text-2xl leading-tight">{it.name}</div>
                  <div className="text-sm text-muted-foreground mt-1">{it.summary}</div>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1"><Globe className="w-3 h-3"/>{it.geography}</span>
                    <span className="inline-flex items-center gap-1"><Coins className="w-3 h-3"/>{it.amount}</span>
                    <span className="inline-flex items-center gap-1"><CalendarClock className="w-3 h-3"/>{it.discipline}</span>
                  </div>
                </div>
                <div className="flex flex-col gap-2 items-end">
                  {it.url && <a href={it.url} target="_blank" rel="noreferrer" className="text-primary text-sm inline-flex items-center gap-1 hover:underline">Visit <ExternalLink className="w-3 h-3"/></a>}
                  <button onClick={()=>del(it.id)} className="text-muted-foreground hover:text-destructive" data-testid={`alert-del-${it.id}`}><Trash2 className="w-4 h-4"/></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
